import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { 
  Mail, Save, Eye, Send, Edit, Copy, RefreshCw, Search, Filter,
  FileCode, Code, List, AlertTriangle, CheckCircle, XCircle,
  ChevronRight, Database, Clock, User, Trash2, Plus
} from 'lucide-react';
import { emailTemplatesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Categoria labels
const CATEGORY_LABELS = {
  customer: { label: 'Cliente', color: 'bg-blue-100 text-blue-800' },
  internal: { label: 'Interno', color: 'bg-purple-100 text-purple-800' }
};

// Variable category labels
const VAR_CATEGORY_LABELS = {
  lead: 'Lead / Cliente',
  appointment: 'Appuntamento',
  estimate: 'Preventivo',
  contract: 'Contratto',
  project: 'Progetto',
  payment: 'Pagamento',
  extra: 'Extra',
  company: 'Azienda',
  staff: 'Staff',
  link: 'Link'
};

const EmailTemplateManager = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'administration';
  
  // State
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [stats, setStats] = useState({ total: 0, customer_count: 0, internal_count: 0 });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Editor
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Test Email
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testTemplateId, setTestTemplateId] = useState(null);
  
  // Seed
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  
  // Load data
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
      if (searchTerm) params.search = searchTerm;
      
      const response = await emailTemplatesApi.list(params);
      setTemplates(response.data.templates || []);
      setStats({
        total: response.data.total || 0,
        customer_count: response.data.customer_count || 0,
        internal_count: response.data.internal_count || 0
      });
    } catch (err) {
      toast.error('Errore nel caricamento dei template');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter, searchTerm]);
  
  const loadVariables = useCallback(async () => {
    try {
      const response = await emailTemplatesApi.variables();
      setVariables(response.data.variables || []);
    } catch (err) {
      console.error('Error loading variables:', err);
    }
  }, []);
  
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);
  
  useEffect(() => {
    loadVariables();
  }, [loadVariables]);
  
  // Handlers
  const handleEdit = (template) => {
    setEditingTemplate({ ...template });
    setEditorOpen(true);
  };
  
  const handleSave = async () => {
    if (!editingTemplate) return;
    
    try {
      setSaving(true);
      const { id, ...data } = editingTemplate;
      await emailTemplatesApi.update(id, {
        name: data.name,
        description: data.description,
        subject: data.subject,
        html_body: data.html_body,
        text_body: data.text_body,
        is_active: data.is_active,
        variables: data.variables
      });
      toast.success('Template salvato con successo');
      setEditorOpen(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Errore nel salvataggio';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleActive = async (template) => {
    try {
      await emailTemplatesApi.update(template.id, { is_active: !template.is_active });
      toast.success(template.is_active ? 'Template disattivato' : 'Template attivato');
      loadTemplates();
    } catch (err) {
      toast.error('Errore nel cambio stato');
    }
  };
  
  const handlePreview = async (template) => {
    try {
      setPreviewLoading(true);
      setPreviewOpen(true);
      const response = await emailTemplatesApi.render(template.id);
      setPreviewData(response.data);
    } catch (err) {
      toast.error('Errore nella generazione anteprima');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const handleOpenTestEmail = (template) => {
    setTestTemplateId(template.id);
    setTestEmail('');
    setTestEmailOpen(true);
  };
  
  const handleSendTest = async () => {
    if (!testEmail || !testTemplateId) {
      toast.error('Inserisci un indirizzo email');
      return;
    }
    
    try {
      setSendingTest(true);
      const response = await emailTemplatesApi.sendTest(testTemplateId, testEmail);
      toast.success(`Email di test inviata a ${testEmail}`);
      setTestEmailOpen(false);
      
      // Show additional info
      if (response.data.warnings?.length > 0) {
        toast.info(`Note: ${response.data.warnings.join(', ')}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Errore nell\'invio email';
      toast.error(detail);
    } finally {
      setSendingTest(false);
    }
  };
  
  const handleSeedDefaults = async () => {
    if (!window.confirm('Importare i template predefiniti?\n\nI template già esistenti non verranno duplicati.')) {
      return;
    }
    
    try {
      setSeedingDefaults(true);
      const response = await emailTemplatesApi.seedDefaults();
      const { created, skipped, total } = response.data;
      toast.success(`Seed completato: ${created} creati, ${skipped} già esistenti`);
      loadTemplates();
    } catch (err) {
      toast.error('Errore nel seed dei template');
    } finally {
      setSeedingDefaults(false);
    }
  };
  
  const handleCopyVariable = (varKey) => {
    const placeholder = `{{${varKey}}}`;
    navigator.clipboard.writeText(placeholder);
    toast.success(`Copiato: ${placeholder}`);
  };
  
  const handleInsertVariable = (varKey) => {
    if (!editingTemplate) return;
    const placeholder = `{{${varKey}}}`;
    // Insert at cursor position would require refs - for now just copy
    handleCopyVariable(varKey);
  };
  
  // Grouped variables by category
  const groupedVariables = variables.reduce((acc, v) => {
    const cat = v.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(v);
    return acc;
  }, {});
  
  // Filtered templates
  const filteredTemplates = templates;
  
  if (loading) {
    return (
      <Card className="card-abitalo">
        <CardContent className="p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#FF7819]" />
          <p className="mt-4 text-gray-500">Caricamento template...</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="card-abitalo">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileCode className="w-5 h-5 text-[#FF7819]" />
                Template Email Configurabili
              </CardTitle>
              <CardDescription>
                Gestisci i template email inviati dal CRM ai clienti e allo staff
              </CardDescription>
            </div>
            {canEdit && (
              <Button 
                onClick={handleSeedDefaults} 
                disabled={seedingDefaults}
                variant="outline"
                className="border-[#FF7819] text-[#FF7819] hover:bg-[#FF7819]/10"
              >
                <Database className="w-4 h-4 mr-2" />
                {seedingDefaults ? 'Importazione...' : 'Importa Default'}
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-abitalo">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#FF7819]">{stats.total}</p>
            <p className="text-sm text-gray-500">Template Totali</p>
          </CardContent>
        </Card>
        <Card className="card-abitalo">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.customer_count}</p>
            <p className="text-sm text-gray-500">Email Cliente</p>
          </CardContent>
        </Card>
        <Card className="card-abitalo">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{stats.internal_count}</p>
            <p className="text-sm text-gray-500">Email Interne</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card className="card-abitalo">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca per nome o chiave..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                <SelectItem value="customer">Cliente</SelectItem>
                <SelectItem value="internal">Interno</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="active">Attivi</SelectItem>
                <SelectItem value="inactive">Disattivi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Template List */}
      <Card className="card-abitalo">
        <CardContent className="p-0">
          {filteredTemplates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Nessun template trovato</p>
              {canEdit && (
                <Button onClick={handleSeedDefaults} className="mt-4 bg-[#FF7819] hover:bg-[#e66a0f]">
                  <Plus className="w-4 h-4 mr-2" />
                  Importa Template Default
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredTemplates.map((template) => (
                <div 
                  key={template.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${!template.is_active ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-[#3C3C3C] truncate">{template.name}</h3>
                        <Badge className={CATEGORY_LABELS[template.category]?.color || 'bg-gray-100'}>
                          {CATEGORY_LABELS[template.category]?.label || template.category}
                        </Badge>
                        {template.is_system_default && (
                          <Badge variant="outline" className="text-xs">Sistema</Badge>
                        )}
                        {!template.is_active && (
                          <Badge variant="secondary" className="bg-red-100 text-red-700">Disattivo</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 font-mono">{template.template_key}</p>
                      <p className="text-sm text-gray-600 mt-1 truncate">{template.subject}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          v{template.version}
                        </span>
                        {template.updated_at && (
                          <span>
                            Aggiornato: {new Date(template.updated_at).toLocaleDateString('it-IT')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(template)}
                        title="Anteprima"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenTestEmail(template)}
                            title="Invia Test"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(template)}
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Switch
                            checked={template.is_active}
                            onCheckedChange={() => handleToggleActive(template)}
                            title={template.is_active ? 'Disattiva' : 'Attiva'}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#FF7819]" />
              Modifica Template: {editingTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Chiave: <code className="bg-gray-100 px-2 py-0.5 rounded">{editingTemplate?.template_key}</code>
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-1">
              {/* Editor Fields */}
              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Template</Label>
                    <Input
                      value={editingTemplate?.name || ''}
                      onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Switch
                        checked={editingTemplate?.is_active || false}
                        onCheckedChange={(checked) => setEditingTemplate(prev => ({ ...prev, is_active: checked }))}
                      />
                      <span className="text-sm">{editingTemplate?.is_active ? 'Attivo' : 'Disattivo'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Oggetto Email</Label>
                  <Input
                    value={editingTemplate?.subject || ''}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Es: Conferma Appuntamento - {{azienda_nome}}"
                  />
                </div>
                
                <Tabs defaultValue="html" className="space-y-2">
                  <TabsList>
                    <TabsTrigger value="html">
                      <Code className="w-4 h-4 mr-2" />
                      HTML
                    </TabsTrigger>
                    <TabsTrigger value="text">
                      <FileCode className="w-4 h-4 mr-2" />
                      Plain Text
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="html" className="mt-2">
                    <Textarea
                      value={editingTemplate?.html_body || ''}
                      onChange={(e) => setEditingTemplate(prev => ({ ...prev, html_body: e.target.value }))}
                      placeholder="<p>Gentile {{cliente_nome}},</p>..."
                      className="font-mono text-sm min-h-[300px]"
                    />
                  </TabsContent>
                  
                  <TabsContent value="text" className="mt-2">
                    <Textarea
                      value={editingTemplate?.text_body || ''}
                      onChange={(e) => setEditingTemplate(prev => ({ ...prev, text_body: e.target.value }))}
                      placeholder="Gentile {{cliente_nome}},..."
                      className="font-mono text-sm min-h-[300px]"
                    />
                  </TabsContent>
                </Tabs>
              </div>
              
              {/* Variables Panel */}
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <List className="w-4 h-4 text-[#FF7819]" />
                      Variabili Disponibili
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Clicca per copiare negli appunti
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="max-h-[400px] overflow-y-auto">
                    {Object.entries(groupedVariables).map(([category, vars]) => (
                      <div key={category} className="mb-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          {VAR_CATEGORY_LABELS[category] || category}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {vars.map((v) => (
                            <button
                              key={v.key}
                              onClick={() => handleInsertVariable(v.key)}
                              className="text-xs bg-gray-100 hover:bg-[#FF7819]/20 hover:text-[#FF7819] px-2 py-1 rounded transition-colors font-mono"
                              title={v.label}
                            >
                              {`{{${v.key}}}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#FF7819] hover:bg-[#e66a0f]">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#FF7819]" />
              Anteprima Template
            </DialogTitle>
            {previewData && (
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={previewData.source === 'db_template' ? 'default' : 'secondary'}>
                  {previewData.source === 'db_template' ? (
                    <><Database className="w-3 h-3 mr-1" /> Database</>
                  ) : (
                    <><AlertTriangle className="w-3 h-3 mr-1" /> Fallback Hardcoded</>
                  )}
                </Badge>
                {previewData.missing_variables?.length > 0 && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    {previewData.missing_variables.length} variabili mancanti
                  </Badge>
                )}
              </div>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {previewLoading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#FF7819]" />
                <p className="mt-4 text-gray-500">Generazione anteprima...</p>
              </div>
            ) : previewData ? (
              <div className="space-y-4">
                {/* Subject */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Oggetto</p>
                  <p className="font-medium">{previewData.subject}</p>
                </div>
                
                {/* Missing Variables Warning */}
                {previewData.missing_variables?.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
                    <p className="text-sm text-orange-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Variabili non trovate nel context: {previewData.missing_variables.join(', ')}
                    </p>
                  </div>
                )}
                
                {/* HTML Preview */}
                <Tabs defaultValue="rendered">
                  <TabsList>
                    <TabsTrigger value="rendered">Anteprima</TabsTrigger>
                    <TabsTrigger value="html">HTML</TabsTrigger>
                    <TabsTrigger value="text">Plain Text</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="rendered" className="mt-2">
                    <div 
                      className="border rounded-lg p-4 bg-white max-h-[400px] overflow-auto"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.html_body) }}
                    />
                  </TabsContent>
                  
                  <TabsContent value="html" className="mt-2">
                    <pre className="border rounded-lg p-4 bg-gray-50 text-xs font-mono overflow-auto max-h-[400px] whitespace-pre-wrap">
                      {previewData.html_body}
                    </pre>
                  </TabsContent>
                  
                  <TabsContent value="text" className="mt-2">
                    <pre className="border rounded-lg p-4 bg-gray-50 text-sm font-mono overflow-auto max-h-[400px] whitespace-pre-wrap">
                      {previewData.text_body}
                    </pre>
                  </TabsContent>
                </Tabs>
              </div>
            ) : null}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#FF7819]" />
              Invia Email di Test
            </DialogTitle>
            <DialogDescription>
              L&apos;email verrà inviata con dati di esempio. L&apos;oggetto avrà prefisso [TEST].
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Indirizzo Email Destinatario</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSendTest} 
              disabled={sendingTest || !testEmail}
              className="bg-[#FF7819] hover:bg-[#e66a0f]"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingTest ? 'Invio...' : 'Invia Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplateManager;
